import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import Nav from "./components/common/Nav";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from "./components/common/PrivateRoute";
import OrganizationPage from "./components/organizations/OrganizationPage";
import SourcesPage from "./components/sources/SourcesPage";
import RoutesPage from "./components/routes/RoutesPage";
import AccountPage from "./components/account/AccountPage";
import { ManageOrganizationDetails } from "./components/organizations/ManageOrganizationDetails";
import DestinationsPage from "./components/destinations/DestinationsPage"
import { ManageRouteDetails } from "./components/routes/ManageRouteDetails";
import { useKeycloakToken, LoginModal } from './components/common/useKeycloak';
import { ManageDestinationDetails } from "./components/destinations/ManageDestinationDetails";
import { ManageAddRouteStepOne } from "./components/routes/ManageAddRouteStepOne";
import { ManageAddRouteStepTwo } from "./components/routes/ManageAddRouteStepTwo";
import { ManageDestinationModal } from "./components/destinations/ManageDestinationModal";
import { HelpProvider } from "./context/HelpContext";
import HelpSidePanel from "./components/common/HelpSidePanel";
import { trackPageView, setAnalyticsUserProperties, getPageTitle } from "./analytics/analytics";
import { RequireContactEmailGate } from "./auth/useRequireContactEmail";
import NotFoundPage from "./components/common/NotFoundPage";
import { resetApiCallLog } from "./api/apiUtils";
import { legacyDestinationsTarget } from "./components/common/legacyPathRedirects";
import LegacyConnectionSubpageRedirect from "./components/destinations/LegacyConnectionSubpageRedirect";
import { TutorialProvider } from "./components/tutorial/TutorialContext";      // GUNDI-5665, temporary
import TutorialOverlay from "./components/tutorial/TutorialOverlay";           // GUNDI-5665, temporary
import TutorialStartModal from "./components/tutorial/TutorialStartModal";     // GUNDI-5665, temporary

// GUNDI-5642: the new portal's whole tree — its own module so that App.tsx can
// load it lazily. Legacy is the default tree now, so nobody downloads this
// chunk until the hidden unlock in the legacy nav switches them over.
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Developer-only API-call counter (GUNDI-5464): start a fresh cumulative
    // tally for each view. No-op output unless the debug flag is set.
    resetApiCallLog();
    trackPageView(location.pathname, getPageTitle(location.pathname));
  }, [location.pathname]);
  return null;
}

// A record can be a destination in one Route and the source of another, so the
// section that lists them is "Connections". The old /destinations and
// /destination URLs are in bookmarks and support docs — keep them working.
const LegacyDestinationsRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={legacyDestinationsTarget(pathname, search)} replace />;
};

// Several routes render the same details screen (/routes/:id, its tabs, and
// the flow-map selection deep links), so React Router hands the mounted screen a
// new :id instead of building a fresh one. Its data is fetched when it mounts, so
// without this it would keep showing the previous Route under the new URL
// until a reload. Keying on the id makes a different Route a new screen,
// while moving between tabs of the same Route still keeps its state.
const RouteDetailsRoute = () => {
  const { route_id } = useParams();
  return <ManageRouteDetails key={route_id} />;
};

// Same for the Connection screen, reached both on its own and from inside a
// Route.
const DestinationDetailsRoute = () => {
  const { dest_id } = useParams();
  return <div><ManageDestinationDetails key={dest_id} /></div>;
};

export default function NewApp() {
  return (
    <div className="new-ui">
          {/* GUNDI-5665 (TEMPORARY): the guided tour. Wraps the tree so the
              banner's "Take a tour" link — rendered by Nav below — sees the
              provider. See the removal manifest in tutorialInterceptor.ts. */}
          <TutorialProvider>
          <HelpProvider>
              <PageViewTracker />
              <RequireContactEmailGate />
              <Nav />
              <HelpSidePanel />
              {/* pt-[143px] = the usual 115px + the 28px version banner. The
                  banner sits inside the FIXED nav wrapper, so it never pushes
                  content down by itself. Since GUNDI-5642 it shows on every
                  page of the new tree — the way back to legacy is always one
                  click away — so the offset is constant. */}
              <div className="px-8 desktop:px-[60px] py-6 pt-[143px]">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <RoutesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <AccountPage />
                      </PrivateRoute>
                    } />
                  
                  {/* workspaces */}
                  <Route
                    path="/workspaces"
                    element={
                      <PrivateRoute>
                        <OrganizationPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/workspaces/:org_id"
                    element={
                      <PrivateRoute>
                        <ManageOrganizationDetails />
                      </PrivateRoute>
                    } />
                  
                  {/* sources */}
                  <Route
                    path="/sources" // sources (list)
                    element={
                      <PrivateRoute>
                        <SourcesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  
                  {/* Connections — every integration record, whatever role it
                      plays in a route. /destinations* redirects here. */}
                   <Route
                    path="/connections"
                    element={
                      <PrivateRoute>
                        <DestinationsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  {/* The Route wizard used to live under /connections/new; those deep
                      links land on a blank Route wizard — wizard state is not
                      restored from the URL. */}
                  <Route path="/connections/new/configureprovider/*" element={<Navigate to="/routes/new" replace />} />
                  <Route path="/connections/new/:connection_id/configuredestinations/*" element={<Navigate to="/routes/new" replace />} />
                  <Route
                    path="/connections/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  {/* The Routes tab has its own URL so the provider→route
                      redirect on the bare path can never swallow it. */}
                  <Route
                    path="/connections/:dest_id/routes"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  {/* Old connection-details tab links (/connections/:id/logs etc.)
                      — a "connection" used to be today's route, so these resolve
                      the provider's route and land on the matching tab. The
                      static /routes and /configuration entries above outrank
                      this param match, so the new-vocabulary pages are safe. */}
                  <Route
                    path="/connections/:dest_id/:subpage"
                    element={
                      <PrivateRoute>
                        <LegacyConnectionSubpageRedirect />
                      </PrivateRoute>
                    } />
                  {/* /connected-systems was this screen's URL on main, so links
                      minted then (tickets, Slack, GA landing pages) still resolve. */}
                  <Route path="/connected-systems" element={<LegacyDestinationsRedirect />} />
                  <Route path="/connected-systems/*" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destinations" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destinations/*" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destination/*" element={<LegacyDestinationsRedirect />} />
                  <Route
                    path="/routes/:route_id"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  {/* Flow-map selection deep links: the segments after /configurations
                      (account/:id[/:section] or flow/:id/:dir[/:section]) are parsed by
                      useSelectionFromUrl — one splat route instead of one route per depth. */}
                  <Route
                    path="/routes/:route_id/configurations/*"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />

                  {/* Add Route flow  */}
                  <Route
                    path="/routes/new"
                    element={
                      <PrivateRoute>
                        <ManageAddRouteStepOne />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/new/configureprovider/:pt_value"
                    element={
                      <PrivateRoute>
                        <ManageAddRouteStepTwo />
                      </PrivateRoute>
                    } />
                  {/* Catch-all 404 */}
                  <Route
                    path="*"
                    element={
                      <PrivateRoute>
                        <NotFoundPage />
                      </PrivateRoute>
                    } />
                </Routes>
                <ToastContainer />
                <Content />
              </div>
          </HelpProvider>
          <TutorialStartModal />
          <TutorialOverlay />
          </TutorialProvider>
    </div>
  );
}

function Content() {
  const [token, showLoginModal, handleLogin, isSessionExpired] = useKeycloakToken();

  useEffect(() => {
    if (token && typeof token === 'string') {
      setAnalyticsUserProperties(token);
    }
  }, [token]);

  return (
    <>
      {(showLoginModal === true) && <LoginModal showLoginModal={showLoginModal} handleLogin={handleLogin} isSessionExpired={isSessionExpired} />}
    </>
  );
}

